/**
 * What the master pane shows when it has no rows to show: the wait, the failure, an empty library,
 * a client list nobody is on yet, and a query that matched nothing.
 *
 * Five exports in one file because they are one concern with a name — what stands in the row box —
 * and they share a stylesheet, a box and a set of rules. Splitting them would put five two-line
 * files beside each other and make the shared geometry a thing to keep in step.
 *
 * ── `pending`, `error` and `empty` are THREE states, never a branch on the data ──────────────
 * `rows.length === 0` cannot tell a read still running from one that answered with nothing, and it
 * cannot tell either from a filter that matched none. The screen switches on the read's own status
 * and on whether the list is narrowed; each of the four below is reached from exactly one of those
 * answers.
 *
 * ── The wait is drawn as the rows it is about to be ──────────────────────────────────────────
 * Not as a sentence, and not as a spinner. Owner, 2 September 2026: "szkielet w kształcie treści".
 * `ListSkeleton` uses the row's own height, inset and grid, so the plans arriving change ink and
 * never geometry — and the gate hides the BARS, never the box, which is why the rows are drawn at
 * their full height whether or not the bars are up.
 *
 * ── An empty card gets a drawing and a tone ──────────────────────────────────────────────────
 * `shared/ui/EmptyState`, never a bare sentence. The tone is `action` on both worded states,
 * because both are a thing that is missing with somewhere to go and make it — which is what
 * separates them from `positive` ("you have dealt with everything") and `neutral` ("a window with
 * nothing in it").
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { DEFAULT_ROSTER_CONTEXT, rosterContextSearch } from '~/entities/client';
import { EmptyState } from '~/shared/ui/EmptyState';
import { PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ember from '~/shared/ui/emberButton.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { planScopeContext, type PlanScope } from '../model/planScope';
import box from '~/shared/ui/listState.module.css';
import styles from './ListStates.module.css';

/** Eight, the canon's own row count — so the wait fills the pane the way the library will. */
const SKELETON_ROWS = 8;

interface ListSkeletonProps {
  /** `shared/lib/useSkeletonGate`'s verdict: 150ms of grace, then 300ms of minimum life. */
  readonly skeleton: boolean;
}

export function ListSkeleton({ skeleton }: ListSkeletonProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.skeletonList} aria-busy="true">
      {/* The word a listener needs; the bars are decorative `<i>`s with no name of their own. */}
      <p className={hidden.srOnly}>{t('web:planLibrary.states.loading')}</p>
      {Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <div key={index} className={styles.skeletonRow}>
          {/* The gate hides the BARS, never the box: every row keeps its 73px whether or not it is
            * drawing anything, so a fast answer draws no skeleton at all and moves nothing. */}
          {skeleton && (
            <>
              <i className={`${skel.bar} ${styles.skeletonCover}`} />
              <span className={styles.skeletonIdentity}>
                <i className={`${skel.bar} ${styles.skeletonName}`} />
                <i className={`${skel.bar} ${styles.skeletonSub}`} />
              </span>
              <i className={`${skel.bar} ${styles.skeletonMode}`} />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

interface ListErrorProps {
  /** Which read failed, because "programów" is the wrong noun over the other list. */
  readonly scope: PlanScope;
  /**
   * The gate's hand-over class, or `''`. This box is one of the things that REPLACE the bars, so it
   * arrives on the same 120ms fade the rows do rather than cutting in beside them.
   */
  readonly className?: string;
  readonly onRetry: () => void;
}

export function ListError({ scope, onRetry, className }: ListErrorProps) {
  const { t } = useTranslation();

  return (
    <div className={[String(box.state), className ?? ''].filter(Boolean).join(' ')}>
      <EmptyState
        mark={<PlanMarkIcon />}
        tone="neutral"
        // The same `context` the no-results hints below already thread through: an assignment read
        // that failed said "Nie udało się wczytać programów", which names the other list.
        title={t('web:planLibrary.states.errorTitle', { context: planScopeContext(scope) })}
        hint={t('web:planLibrary.states.errorMessage')}
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onRetry}>
          {t('web:planLibrary.states.retry')}
        </button>
      </div>
    </div>
  );
}

interface ListEmptyProps {
  readonly onCreate: () => void;
  /** The gate's hand-over class, or `''` — see `ListError`. */
  readonly className?: string;
}

export function ListEmpty({ onCreate, className }: ListEmptyProps) {
  const { t } = useTranslation();

  return (
    <div className={[String(box.state), className ?? ''].filter(Boolean).join(' ')}>
      <EmptyState
        mark={<PlanMarkIcon />}
        tone="action"
        title={t('web:planLibrary.empty.title')}
        hint={t('web:planLibrary.empty.message')}
      />
      <div className={box.actions}>
        <button type="button" className={`${ember.ember} ${box.primary}`} onClick={onCreate}>
          {t('web:planCreate.open')}
        </button>
      </div>
    </div>
  );
}

interface ClientPlansEmptyProps {
  /** The gate's hand-over class, or `''` — see `ListError`. */
  readonly className?: string;
}

/**
 * Nobody is running a plan yet — the clients scope's own empty, which is a different fact from an
 * empty library and needs a different way out.
 *
 * `tone: 'neutral'`, not `action`: the trainer HAS programmes and the thing to do about this is not
 * on this screen. The way out is the roster, because a plan is assigned from a client rather than
 * from the library, and that link is the whole hint.
 *
 * The roster's default list context is written explicitly rather than left off: `rosterContextSearch`
 * is the one place that knows a default is never serialised, and a link that spelled the default out
 * would give the roster two addresses.
 */
export function ClientPlansEmpty({ className }: ClientPlansEmptyProps) {
  const { t } = useTranslation();

  return (
    <div className={[String(box.state), className ?? ''].filter(Boolean).join(' ')}>
      <EmptyState
        mark={<PlanMarkIcon />}
        tone="neutral"
        title={t('web:planLibrary.clientPlans.empty.title')}
        hint={t('web:planLibrary.clientPlans.empty.message')}
      />
      <div className={box.actions}>
        <Link
          className={box.ghost}
          to="/klienci"
          search={rosterContextSearch(DEFAULT_ROSTER_CONTEXT)}
        >
          {t('web:planLibrary.clientPlans.empty.action')}
        </Link>
      </div>
    </div>
  );
}

interface ListNoResultsProps {
  /** The phrase in the search box, or the empty string when only the panel is narrowing the list. */
  readonly term: string;
  /** Whether any of the panel's facets is on, which decides which of the three sentences applies. */
  readonly facets: boolean;
  /** Which list matched nothing — a programme in one scope, a client's plan in the other. */
  readonly scope: PlanScope;
  readonly onClear: () => void;
  /** The gate's hand-over class, or `''` — see `ListError`. */
  readonly className?: string;
}

/**
 * The query matched nothing.
 *
 * Three sentences rather than one, because the trainer has to know WHICH of the two things they set
 * is hiding the rows: a phrase they can see in the field, facets they cannot see behind a closed
 * panel, or both. A single "nothing matched" beside a panel with a dot on it is the case where they
 * go looking for a plan that is right there.
 *
 * `Wyczyść filtry` clears BOTH. The button says filters and the panel's own footer button says
 * `Wyczyść`; a control here that emptied only the field would leave the trainer looking at the same
 * empty list under a button they had just pressed.
 */
export function ListNoResults({ term, facets, scope, onClear, className }: ListNoResultsProps) {
  const { t } = useTranslation();
  const hintKey =
    term !== '' && facets ? 'searchAndFilters' : term !== '' ? 'search' : 'filters';

  return (
    <div className={[String(box.state), className ?? ''].filter(Boolean).join(' ')}>
      <EmptyState
        mark={<PlanMarkIcon />}
        tone="neutral"
        title={t('web:planLibrary.noResults.title')}
        hint={t(`web:planLibrary.noResults.${hintKey}`, {
          term,
          context: planScopeContext(scope),
        })}
      />
      <div className={box.actions}>
        <button type="button" className={box.ghost} onClick={onClear}>
          {t('web:planLibrary.noResults.clear')}
        </button>
      </div>
    </div>
  );
}
